import React, { type FC } from 'react';
import ScrollableAnchor from 'react-scrollable-anchor';
import styled from 'styled-components';
import { Grid, Paragraph } from '../../UIKit/system';
import Title from '../../title';
import RichTextField from '../../rich-text-field';
import Button from '../../button';
import { getThemeColor } from '../../../utils/styles';
import Container from '../../container';
import LazyImage from '../../lazy-image';
import { getFluidGatsbyImage } from '../../../utils/gatsby-storyblok-image-fixed';
import parseStoryblokLink from '../../../utils/parse-storyblok-link';

interface ProductOverviewProps {
  title: string;
  highlights?: {
    _uid: string;
    title: string;
    image: Record<string, any>;
  }[];
  features?: {
    _uid: string;
    component: 'productFeature';
    title: string;
    content: Record<string, any>;
    link: unknown;
    linkText: string;
  }[];
  compareLink?: {
    title: string;
    link: Record<string, any>;
  };
}

export const ProductOverview: FC<ProductOverviewProps> = (props) => {
  const { title, highlights, features, compareLink } = props;

  if (!highlights && !features) return null;

  return (
    <ScrollableAnchor id="overview">
      <Grid backgroundColor="black" gridAutoFlow="row">
        <Container large py={[36, 72]}>
          <Title as="h2" color="white" pb={40} fontSizeMedium>
            {title}
          </Title>
          {highlights && (
            <StyledHighlightsGrid>
              {highlights.map((highlight) => (
                <StyledHighlight key={highlight._uid}>
                  <StyledImageContainer>
                    <LazyImage fluid={getFluidGatsbyImage(highlight.image)} />
                  </StyledImageContainer>
                  <Paragraph as="p" fontWeight={700} fontSizeSmall>
                    {highlight.title}
                  </Paragraph>
                </StyledHighlight>
              ))}
            </StyledHighlightsGrid>
          )}

          {features && (
            <StyledFeaturesGrid>
              {features.map((feature) => (
                <StyledFeature key={feature._uid}>
                  <Title as="h2" color="white" fontSizeSmall>
                    {feature.title}
                  </Title>
                  <Paragraph color="silver" fontSize={16}>
                    <RichTextField data={feature.content} />
                  </Paragraph>
                  {feature.linkText && (
                    <Button
                      to={parseStoryblokLink(feature.link)}
                      borderColorPrimary
                      alignSelf="end"
                    >
                      {feature.linkText}
                    </Button>
                  )}
                </StyledFeature>
              ))}
            </StyledFeaturesGrid>
          )}

          {compareLink && (
            <Grid paddingTop={40} justifyContent="center">
              <Button to={parseStoryblokLink(compareLink.link)} inDarkBlock>
                {compareLink?.title}
              </Button>
            </Grid>
          )}
        </Container>
      </Grid>
    </ScrollableAnchor>
  );
};

const StyledHighlightsGrid = styled(Grid).attrs(() => ({
  gridAutoFlow: 'row',
  gridTemplateColumns: [undefined, '1fr 1fr'],
  gridGap: 20,
  pb: [32, 64]
}))``;

const StyledHighlight = styled(Grid).attrs(() => ({
  alignItems: 'center',
  gridTemplateColumns: 'auto 1fr',
  gridGap: 20,
  color: 'white',
  px: 24,
  py: 20,
  borderRadius: 'var(--Radius-Radius-3, 16px)'
}))`
  background-color: rgb(from ${getThemeColor('white')} 255 255 255 / 5%);
`;

const StyledImageContainer = styled.div`
  height: 40px;
  width: 40px;
`;

const StyledFeaturesGrid = styled(Grid).attrs(() => ({
  gridTemplateColumns: [undefined, 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
  gridGap: 20
}))``;

const StyledFeature = styled(Grid).attrs(() => ({
  gridTemplateRows: 'auto 1fr auto',
  gridGap: 24,
  p: 24,
  borderRadius: 'var(--Radius-Radius-3, 16px)'
}))`
  background-color: rgb(from ${getThemeColor('white')} 255 255 255 / 5%);
`;
